import type { Employee, EmployeeRole, Shop } from "@/types/v2";
import {
  getMergeFieldChanges,
  getRoleFromShopLists,
  type MergeFieldChanges,
} from "@/lib/employees";
import { user as userActions } from "@/repositories/user";
import { employee as employeeActions } from "@/repositories/employee";

export type MergeRolePlan =
  | { status: "copy"; role: EmployeeRole }
  | { status: "link"; role: EmployeeRole; uid: string }
  | { status: "skip"; reason: string };

/** The merge was saved, but copying the manual employee's role failed. */
export class RoleNotCopiedError extends Error {
  cause: unknown;
  constructor(cause: unknown) {
    super("The employees were merged, but the role could not be copied.");
    this.name = "RoleNotCopiedError";
    this.cause = cause;
  }
}

const skip = (reason: string): MergeRolePlan => ({ status: "skip", reason });

/**
 * Decide whether the manual employee's role can be copied to the synced one.
 * Roles live on login accounts (uids) in the shop's role lists, so this looks
 * up both logins. Never throws for missing accounts; those become skips.
 */
export async function loadMergeRolePlan({
  shop,
  manual,
  synced,
}: {
  shop: Shop | undefined;
  manual: Employee;
  synced: Employee;
}): Promise<MergeRolePlan> {
  const manualLogin = await userActions
    .getId(manual.email)
    .catch(() => undefined);
  const role = getRoleFromShopLists(shop, manualLogin?.data?.uid);
  if (!role) return skip(`${manual.firstName} has no role in this shop.`);

  const syncedLogin = await userActions
    .getId(synced.email)
    .catch(() => undefined);
  if (!syncedLogin?.data) {
    return skip(
      `${synced.firstName} has no login yet. Create and activate it, then set their role.`
    );
  }
  const disabledSkip = () =>
    skip(
      `${synced.firstName}'s login is disabled. Activate it, then set their role.`
    );
  // Same email means one shared login: the role is already on that login, so
  // the synced employee only needs the link to it.
  if (syncedLogin.data.uid === manualLogin?.data?.uid) {
    if (syncedLogin.data.disabled) return disabledSkip();
    return { status: "link", role, uid: syncedLogin.data.uid };
  }
  if (getRoleFromShopLists(shop, syncedLogin.data.uid)) {
    return skip(`${synced.firstName} already has a role.`);
  }
  if (syncedLogin.data.disabled) return disabledSkip();
  return { status: "copy", role };
}

/**
 * Merge a manual employee into a synced one, then copy the role if possible.
 * The role plan is always reloaded here so a stale review screen is never
 * trusted. A merge write failure propagates unchanged (no role is granted);
 * a role grant failure after a successful merge throws RoleNotCopiedError.
 */
export async function executeMerge({
  shopId,
  shop,
  manual,
  synced,
}: {
  shopId: string;
  shop: Shop | undefined;
  manual: Employee;
  synced: Employee;
}): Promise<{ changes: MergeFieldChanges; rolePlan: MergeRolePlan }> {
  const rolePlan = await loadMergeRolePlan({ shop, manual, synced });
  const changes: MergeFieldChanges = {
    ...getMergeFieldChanges(manual, synced),
    // Same login: link it in the same atomic batch as the merge.
    ...(rolePlan.status === "link"
      ? { shopHeroAuthUid: rolePlan.uid, shopHeroRole: rolePlan.role }
      : {}),
  };
  await employeeActions.mergeManualIntoSynced(
    shopId,
    manual.id,
    synced.id,
    changes,
    { manualAlreadyDeleted: !!manual._deleted }
  );
  if (rolePlan.status === "copy") {
    try {
      await employeeActions.updateEmployeeRole(
        shopId,
        synced.id,
        rolePlan.role
      );
    } catch (error) {
      throw new RoleNotCopiedError(error);
    }
  }
  return { changes, rolePlan };
}
